/**
 * Transitional types for the Precursor/Barrier tab components.
 *
 * These types were formerly defined in hooks/irp/useNodeMappings.ts alongside
 * the old progressive-load hook. They are preserved here so that sub-components
 * (NodeInteractionsPanel, JustificationAnalysisPanel, NodeStatsSummary,
 * ConfidenceDistributionChart, nodeReportExporter) continue to compile while
 * Tasks 13–15 rewire them to use IrpMappedReportDTO from the BFF.
 *
 * TODO (Task 13): Replace MappedReport with IrpMappedReportDTO throughout.
 */

import NewOccurrenceReport from './new-occurrence-report';
import {ReportMappingDTO} from './integrated-risk-picture';

export interface MappedReport {
    reportId: number;
    report: NewOccurrenceReport;
    mapping: ReportMappingDTO;
    confidence: number;
    isManual: boolean;
    mappingDate?: string;
    justification?: string;
    positiveInteraction?: boolean;
}

export interface ConfidenceDistribution {
    '70-75': number;
    '75-80': number;
    '80-85': number;
    '85-90': number;
    '90-95': number;
    '95-100': number;
    'below70': number;
}

export interface ReportTrendData {
    currentPeriodCount: number;
    previousPeriodCount: number;
    percentageChange: number;
    trendDirection: 'up' | 'down' | 'stable';
}

export interface ReportSourceBreakdown {
    [sourceName: string]: number;
}

export interface TimelineData {
    [yearMonth: string]: {
        count: number;
        aiCount: number;
        manualCount: number;
        averageConfidence: number;
    };
}

export interface DateRangeStatistics {
    earliestDate: Date | null;
    latestDate: Date | null;
    dateRangeInDays: number;
    timelineData: TimelineData;
}

export interface NodeMappingStatistics {
    totalReports: number;
    averageConfidence: number;
    medianConfidence: number;
    aiMappingsCount: number;
    manualMappingsCount: number;
    confidenceDistribution: ConfidenceDistribution;
    trendData: ReportTrendData;
    sourceBreakdown: ReportSourceBreakdown;
    dateRangeStats: DateRangeStatistics;
}

export const EMPTY_NODE_MAPPING_STATISTICS: NodeMappingStatistics = {
    totalReports: 0,
    averageConfidence: 0,
    medianConfidence: 0,
    aiMappingsCount: 0,
    manualMappingsCount: 0,
    confidenceDistribution: {
        '70-75': 0,
        '75-80': 0,
        '80-85': 0,
        '85-90': 0,
        '90-95': 0,
        '95-100': 0,
        'below70': 0,
    },
    trendData: {
        currentPeriodCount: 0,
        previousPeriodCount: 0,
        percentageChange: 0,
        trendDirection: 'stable',
    },
    sourceBreakdown: {},
    dateRangeStats: {
        earliestDate: null,
        latestDate: null,
        dateRangeInDays: 0,
        timelineData: {},
    },
};
